"use strict";
// Owns everything about "is this browser signed in right now" that
// flix-identity-api.ts deliberately doesn't: CSRF token caching, silent
// refresh-and-retry on a 401, and deciding when a session is genuinely
// over. Every Account Hub screen should call through here, never through
// window.FlixIdentityAPI directly, so there is exactly one place this
// policy lives.
(function () {
    var state = "unknown";
    var csrfToken = null;
    /**
     * Where a browser goes once its session is over. Null means "the account
     * page in the language being browsed", worked out at the moment it is
     * needed — Shopify.routes.root is "/" or "/en/", so the same path lands on
     * the page in the visitor's own language instead of always the Arabic one.
     */
    var signedOutRedirectUrl = null;
    function accountPageUrl() {
        var shopify = window.Shopify;
        var rootPath = (shopify && shopify.routes && shopify.routes.root) || "/";
        return (rootPath.charAt(rootPath.length - 1) === "/" ? rootPath : rootPath + "/") + "pages/account";
    }
    var refreshInFlight = null;
    function configure(config) {
        signedOutRedirectUrl = config.signedOutRedirectUrl;
    }
    function setState(next) {
        if (state === next)
            return;
        state = next;
        document.dispatchEvent(new CustomEvent("flix:auth:state-changed", { detail: { state: next } }));
    }
    function isFlixApiError(err) {
        return typeof err === "object" && err !== null && "status" in err && "code" in err;
    }
    async function ensureCsrfToken() {
        if (csrfToken)
            return csrfToken;
        var token = await window.FlixIdentityAPI.getCsrf();
        csrfToken = token;
        return token;
    }
    async function attemptRefresh() {
        if (refreshInFlight)
            return refreshInFlight;
        refreshInFlight = (async function () {
            try {
                // GET /auth/csrf itself requires a still-valid access token, so
                // this only ever succeeds here if a token was already cached
                // earlier in the page's life (checkSession() fetches one
                // opportunistically while still signed in, specifically so it's
                // available later). A hard reload after the access token has
                // already expired has no cached token to offer and this correctly
                // fails closed — see docs note in the report handed back with
                // this work.
                var token = await ensureCsrfToken();
                await window.FlixIdentityAPI.refresh({ csrfToken: token });
                return true;
            }
            catch (err) {
                return false;
            }
            finally {
                refreshInFlight = null;
            }
        })();
        return refreshInFlight;
    }
    /** Runs `run`; on a 401, tries exactly one silent refresh and retries `run` once more. Returns null (never throws for a 401) if that still fails — the caller decides what "give up" means. */
    async function tryWithRefresh(run) {
        try {
            return await run();
        }
        catch (err) {
            if (!isFlixApiError(err) || err.status !== 401) {
                throw err;
            }
            var refreshed = await attemptRefresh();
            if (!refreshed) {
                return null;
            }
            try {
                return await run();
            }
            catch (retryErr) {
                if (isFlixApiError(retryErr) && retryErr.status === 401) {
                    return null;
                }
                throw retryErr;
            }
        }
    }
    function handleSessionExpired() {
        csrfToken = null;
        setState("signed-out");
        document.dispatchEvent(new CustomEvent("flix:auth:session-expired"));
        window.location.href = signedOutRedirectUrl !== null && signedOutRedirectUrl !== void 0 ? signedOutRedirectUrl : accountPageUrl();
    }
    /**
     * The initial "am I signed in" probe — a page load with no session at
     * all must resolve to null quietly, not redirect (this runs ON
     * /pages/account itself; redirecting there would loop). Only
     * authorizedCall's post-session failures count as a session *ending*.
     */
    async function checkSession() {
        var profile = await tryWithRefresh(function () {
            return window.FlixIdentityAPI.getMe();
        });
        if (profile === null) {
            setState("signed-out");
            return null;
        }
        setState("signed-in");
        ensureCsrfToken().catch(function () {
            /* fetched again on demand by the first mutation that needs it */
        });
        return profile;
    }
    async function authorizedCall(run) {
        var result = await tryWithRefresh(run);
        if (result === null) {
            handleSessionExpired();
            throw new Error("FLIX_SESSION_EXPIRED");
        }
        return result;
    }
    function getProfile() {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.getMe();
        });
    }
    function updateProfile(patch) {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.updateMe(Object.assign({}, patch, { csrfToken: token }));
        });
    }
    /**
     * The avatar goes through here like every other write, so a 401 mid-flight
     * gets the same transparent refresh and a session expiry reaches the same
     * one place that handles it.
     */
    function uploadAvatar(file) {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.uploadAvatar({ file: file, csrfToken: token });
        });
    }
    function removeAvatar() {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.removeAvatar({ csrfToken: token });
        });
    }
    function getMarketing() {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.getMarketing();
        });
    }
    function updateMarketing(subscribed) {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.updateMarketing({
                subscribed: subscribed,
                csrfToken: token,
            });
        });
    }
    function startLinkEmail(email) {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.startLinkEmail({ email: email, csrfToken: token });
        });
    }
    function verifyLinkEmail(challengeId, code) {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.verifyLinkEmail({
                challengeId: challengeId,
                code: code,
                csrfToken: token,
            });
        });
    }
    function listSessions() {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.listSessions();
        });
    }
    async function revokeSession(sessionId) {
        var token = await ensureCsrfToken();
        await authorizedCall(function () {
            return window.FlixIdentityAPI.revokeSession({ sessionId: sessionId, csrfToken: token });
        });
    }
    function listOrders(input) {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.listOrders(input);
        });
    }
    function getOrderDetail(orderId) {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.getOrderDetail(orderId);
        });
    }
    function listDownloads() {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.listDownloads();
        });
    }
    function listOrderFiles() {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.listOrderFiles();
        });
    }
    async function revokeAuthMethod(authMethodId) {
        var token = await ensureCsrfToken();
        await authorizedCall(function () {
            return window.FlixIdentityAPI.revokeAuthMethod({
                authMethodId: authMethodId,
                csrfToken: token,
            });
        });
    }
    function startAccountDeletion() {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.startAccountDeletion({ csrfToken: token });
        });
    }
    function verifyAccountDeletion(challengeId, code) {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.verifyAccountDeletion({
                challengeId: challengeId,
                code: code,
                csrfToken: token,
            });
        });
    }
    /**
     * Ends the account. Deliberately does NOT go through authorizedCall's
     * refresh-and-retry: the session is meant to die here, so a 401 on the
     * way out is the expected end state rather than something to recover
     * from by silently refreshing a grant the server just revoked.
     */
    async function deleteAccount() {
        var token = await ensureCsrfToken();
        await window.FlixIdentityAPI.deleteAccount({ csrfToken: token });
        csrfToken = null;
        setState("signed-out");
    }
    function detectClaim() {
        return authorizedCall(function () {
            return window.FlixIdentityAPI.detectClaim();
        });
    }
    function confirmClaim() {
        return authorizedCall(async function () {
            var token = await ensureCsrfToken();
            return window.FlixIdentityAPI.confirmClaim({ csrfToken: token });
        });
    }
    /**
     * Ends the session. Deliberately does not navigate.
     *
     * It used to send the browser to the signed-out page, which made sense
     * when signing out could only be done from the account page itself. It
     * happens in the overlay now, over whatever the person was reading, and
     * throwing that page away to prove the session ended would discard the
     * thing they were actually doing. The caller decides what to show next;
     * on the storefront that is the same overlay, in its signed-out state.
     *
     * A session ENDING on its own is different and still redirects — see
     * handleSessionExpired, which fires where a page is already showing
     * data the viewer is no longer entitled to.
     */
    async function signOut() {
        try {
            var token = await ensureCsrfToken();
            await window.FlixIdentityAPI.signOut({ csrfToken: token });
        }
        catch (err) {
            // The server clears the session cookies unconditionally even when
            // this call fails to complete cleanly (e.g. it was already signed
            // out) — the client-side exit must not get stuck on that.
        }
        csrfToken = null;
        setState("signed-out");
    }
    function getState() {
        return state;
    }
    /**
     * Signing in inside the overlay.
     *
     * The session is established on the response to `verify`, so the only
     * thing left to do is ask who we now are — `checkSession` does that and
     * publishes the state change every other part of the UI listens for. No
     * navigation, no reload, and nothing about the session passes through
     * here: it is in cookies this script cannot read.
     */
    async function startEmailSignIn(email) {
        var token = await ensureCsrfToken();
        return window.FlixIdentityAPI.startEmailSignIn({ email: email, csrfToken: token });
    }
    async function resendEmailSignIn(challengeId) {
        var token = await ensureCsrfToken();
        return window.FlixIdentityAPI.resendEmailSignIn({
            challengeId: challengeId,
            csrfToken: token,
        });
    }
    async function verifyEmailSignIn(challengeId, code) {
        var token = await ensureCsrfToken();
        await window.FlixIdentityAPI.verifyEmailSignIn({
            challengeId: challengeId,
            code: code,
            csrfToken: token,
        });
        // That token was minted for an anonymous caller; the session is a new
        // one, so the next write needs a token issued under it.
        csrfToken = null;
        return checkSession();
    }
    window.FlixAuthClient = {
        configure: configure,
        checkSession: checkSession,
        getProfile: getProfile,
        updateProfile: updateProfile,
        uploadAvatar: uploadAvatar,
        removeAvatar: removeAvatar,
        getMarketing: getMarketing,
        updateMarketing: updateMarketing,
        startEmailSignIn: startEmailSignIn,
        resendEmailSignIn: resendEmailSignIn,
        verifyEmailSignIn: verifyEmailSignIn,
        startLinkEmail: startLinkEmail,
        verifyLinkEmail: verifyLinkEmail,
        listSessions: listSessions,
        revokeSession: revokeSession,
        listOrders: listOrders,
        getOrderDetail: getOrderDetail,
        listDownloads: listDownloads,
        listOrderFiles: listOrderFiles,
        revokeAuthMethod: revokeAuthMethod,
        startAccountDeletion: startAccountDeletion,
        verifyAccountDeletion: verifyAccountDeletion,
        deleteAccount: deleteAccount,
        signOut: signOut,
        getState: getState,
        detectClaim: detectClaim,
        confirmClaim: confirmClaim,
    };
    // Probes session state once on every page load (not just when the Hub
    // opens) so sitewide UI — today just the header account icon — can
    // reflect it via the flix:auth:state-changed event without depending on
    // script load order or calling back into this file directly. Per the
    // approved decision, this is a real GET /me on every page view rather
    // than a cheaper readable cookie hint — deliberate, not an oversight.
    checkSession().catch(function () {
        /* a network failure here just leaves state "unknown" — the header
           icon's neutral default already covers that. */
    });
})();
